import { Alerts } from 'constants/alerts';
import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { useApiContext } from 'context/apiContext';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { useLocalDatagrid } from 'hooks/useLocalDatagrid';
import { useLocalDatagridPagination } from 'hooks/useLocalDatagridPagination';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { OrdersReportListCommissionField } from 'pages/Finance/OrdersReport/OrdersReportList/OrdersReportListCommissionField/OrdersReportListCommissionField';
import { OrdersReportListRuleField } from 'pages/Finance/OrdersReport/OrdersReportList/OrdersReportListRuleField/OrdersReportListRuleField';
import { OrdersReportListTotalAmountField } from 'pages/Finance/OrdersReport/OrdersReportList/OrdersReportListTotalAmountField/OrdersReportListTotalAmountField';
import { RecordContextProvider, useListContext } from 'react-admin';
import { EmptyListAlert } from 'shared/mui/Alert/EmptyListAlert/EmptyListAlert';
import { FinanceUserAlert } from 'shared/mui/Alert/FinanceUserAlert/FinanceUserAlert';
import { Box } from 'shared/mui/Box';
import { Chip } from 'shared/mui/Chips';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { TablePagination } from 'shared/mui/Table';
import { Typography } from 'shared/mui/Typography';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import {
  ChipField,
  TextField,
  FunctionField,
  NumberField,
  DateTimeField,
  CopyToClipboardLinkField,
  CopyToClipboardField,
} from 'shared/react-admin/Fields';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { ReferenceLink } from 'shared/react-admin/ReferenceFields/ReferenceLink/ReferenceLink';
import { CommissionShowDialog } from 'shared/widgets/CommissionShowDialog/CommissionShowDialog';
import { MerchantAccountShowDialog } from 'shared/widgets/MerchantAccountShowDialog/MerchantAccountShowDialog';
import { Commission } from 'types/embedded/commission';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { Order } from 'types/embedded/order';
import { OrderReportData } from 'types/embedded/orderReport';
import { getId } from 'utils';

import {
  DatagridConfigurableStyled,
  ImageFieldStyled,
} from './OrdersReportListLocalDatagrid.styled';
type Props = {
  orders: OrderReportData[];
  total: number;
  isLoading: boolean;
  timezone?: string;
  toggleDrawer: (open: boolean) => () => void;
};

export const OrdersReportListLocalDatagrid: React.FC<Props> = ({
  total,
  isLoading,
  orders = [],
  timezone = DEFAULT_TIMEZONE,
  toggleDrawer,
}) => {
  const { hasResourceAccess } = usePermissions();
  const { filterValues } = useListContext();
  const { createPathWithQuery } = useCreatePathWithQuery();

  const {
    record: merchantAccount,
    handleOpenRecordModal: handleOpenMerchantAccountModal,
    handleCloseRecordModal: handleCloseMerchantAccountModal,
    openRecordModal: openMerchantAccountModal,
  } = useRecordModal<MerchantAccount>();

  const { record, handleOpenRecordModal, handleCloseRecordModal, openRecordModal } =
    useRecordModal<Commission>();

  const { sortedData, sort, setSort } = useLocalDatagrid({
    data: orders,
    defaultSort: {
      field: 'createdAt',
      order: 'ASC',
    },
  });

  const { dataPerPage, ...paginationProps } = useLocalDatagridPagination<OrderReportData>({
    data: sortedData,
    defaultPage: 0,
    defaultRowsPerPage: 10,
  });
  const { user } = useApiContext();

  const showEmptyListAlert = Boolean(total === 0 && !Object.keys(filterValues).length);

  if (user.alerts.includes(Alerts.EmptyProviderAccountAccess)) {
    return <FinanceUserAlert firstName={user.firstName} lastName={user.lastName} />;
  }

  if (isLoading) {
    return <DatagridSkeleton columnsCount={16} />;
  }

  if (showEmptyListAlert) {
    return <EmptyListAlert onClick={toggleDrawer(true)} />;
  }

  return (
    <>
      {record && (
        <CommissionShowDialog
          commission={record}
          onClose={handleCloseRecordModal}
          open={openRecordModal}
        />
      )}
      <DatagridConfigurableStyled
        bulkActionButtons={false}
        data={dataPerPage}
        resizableColumns={true}
        setSort={setSort}
        sort={sort}
        sx={{ minWidth: '100%' }}
      >
        <CopyToClipboardLinkField
          cellClassName="resize-originId"
          getLink={(record: OrderReportData) => {
            return createPathWithQuery({
              resource: Resources.Orders,
              id: encodeURIComponent(record.id),
              type: 'show',
            });
          }}
          headerClassName="resize-originId"
          label="Kubera ID"
          sortBy="id"
          source="originId"
          target="_blank"
        />
        <DateTimeField
          cellClassName="resize-createdAt"
          headerClassName="resize-createdAt"
          label="Создан"
          resource="orders"
          source="createdAt"
          timezone={timezone}
        />
        <DateTimeField
          cellClassName="resize-completedAt"
          emptyText={EMPTY_FIELD}
          headerClassName="resize-completedAt"
          label="Исполнен"
          resource="orders"
          source="completedAt"
          timezone={timezone}
        />
        <NumberField
          cellClassName="resize-amount"
          headerClassName="resize-amount"
          label="Сумма заказа"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          resource="orders"
          source="amount"
        />
        {hasResourceAccess(Resources.Currencies) && (
          <FunctionField
            cellClassName="resize-currency"
            headerClassName="resize-currency"
            label="Валюта"
            render={(record: OrderReportData) => record?.currency}
            source="currency"
            sx={{ fontWeight: 500 }}
          />
        )}
        <OrdersReportListCommissionField
          cellClassName="resize-commissionValueByCurrencyOrder"
          headerClassName="resize-commissionValueByCurrencyOrder"
          label="Комиссия по заказу"
          source="commissionValueByCurrencyOrder"
        />
        <OrdersReportListCommissionField
          cellClassName="resize-commissionValueByCurrencyCommission"
          headerClassName="resize-commissionValueByCurrencyCommission"
          label="Комиссия по правилу"
          source="commissionValueByCurrencyCommission"
        />
        {hasResourceAccess(Resources.Commissions) && (
          <FunctionField
            cellClassName="resize-commission-rule"
            headerClassName="resize-commission-rule"
            label="Правило, %"
            render={() => {
              return (
                <OrdersReportListRuleField
                  label="Правило, %"
                  onClick={handleOpenRecordModal}
                  source="commission"
                />
              );
            }}
          />
        )}
        <FunctionField
          cellClassName="resize-totalAmount"
          headerClassName="resize-totalAmount"
          label="Итого по заказу"
          render={() => {
            return <OrdersReportListTotalAmountField />;
          }}
        />

        <FunctionField
          cellClassName="resize-direction"
          headerClassName="resize-direction"
          label="Направление"
          render={({ direction }: Order) => {
            return <Chip label={direction} />;
          }}
        />
        <FunctionField
          cellClassName="resize-direction"
          headerClassName="resize-merchant"
          label="Мерчант"
          render={(record: Order) => (
            <RecordContextProvider value={record.merchant}>
              <ReferenceLink recordId="@id" resource="merchants" type="show">
                <ChipField source="name" />
              </ReferenceLink>
            </RecordContextProvider>
          )}
          sortable={false}
          source="merchant"
        />
        <ReferenceField
          cellClassName="resize-merchantAccount"
          headerClassName="resize-merchantAccount"
          label="Счет мерчанта"
          link={false}
          reference="merchant_accounts"
          resource="merchant_accounts"
          sortable={false}
          source="merchantAccount.@id"
        >
          <FunctionField
            render={(record: MerchantAccount) => (
              <Typography
                color="secondary"
                onClick={() => handleOpenMerchantAccountModal(record)}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                variant="body2"
              >
                {record.name}
              </Typography>
            )}
          />
        </ReferenceField>
        <TextField
          cellClassName="resize-gateway"
          headerClassName="resize-gateway"
          label="Шлюз"
          sortable={false}
          source="gateway"
        />
        <FunctionField
          cellClassName="resize-method"
          headerClassName="resize-method"
          label="Метод"
          render={(record: OrderReportData) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {hasResourceAccess(Resources.MethodLogos) && (
                <ImageFieldStyled source="methodLogo.filePath" />
              )}
              <Typography variant="body2">{record?.method}</Typography>
            </Box>
          )}
        />
        <CopyToClipboardField
          cellClassName="resize-merchantReference"
          getValue={getId}
          headerClassName="resize-merchantReference"
          isTrimValue
          label="Мерчант ID"
          source="merchantReference"
        />
        <CopyToClipboardField
          cellClassName="resize-providerReferenceId"
          getValue={getId}
          headerClassName="resize-providerReferenceId"
          isTrimValue
          label="Провайдер ID"
          source="providerReferenceId"
        />
      </DatagridConfigurableStyled>
      {total > 0 && (
        <TablePagination count={total} sx={{ display: 'block' }} {...paginationProps} />
      )}
      {merchantAccount && (
        <MerchantAccountShowDialog
          merchantAccount={merchantAccount}
          onClose={handleCloseMerchantAccountModal}
          open={openMerchantAccountModal}
        />
      )}
    </>
  );
};
